"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SocketManager = void 0;
const models_1 = require("@creature-chess/models");
const socket_io_client_1 = require("socket.io-client");
const state_1 = require("~/store/menu/state");
const GameConnection_1 = require("./GameConnection");
const LobbyConnection_1 = require("./LobbyConnection");
const sessionSources_1 = require("./sessionSources");
// How long to wait for the server's reconnect verdict before giving up and
// falling through to the menu.
const RECONNECT_TIMEOUT_MS = 5000;
class SocketManager {
    constructor(dispatch, gameConnectionHolder, lobbyConnectionHolder, accountIdHolder) {
        this.dispatch = dispatch;
        this.gameConnectionHolder = gameConnectionHolder;
        this.lobbyConnectionHolder = lobbyConnectionHolder;
        this.accountIdHolder = accountIdHolder;
        this.socket = null;
        this.cleanupConnectionListeners = null;
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.dispatch(state_1.MenuCommands.setLoadingMessage("Connecting..."));
            const request = yield (0, sessionSources_1.resolveHandshake)();
            if (!request) {
                this.dispatch(state_1.MenuCommands.setLoadingMessage("ERROR: Failed to open session!"));
                return;
            }
            try {
                yield this.connectSocket(request);
            }
            catch (error) {
                console.error("error getting socket", error);
                return;
            }
            this.listenForConnection();
            (_a = this.socket) === null || _a === void 0 ? void 0 : _a.emit("play");
        });
    }
    /**
     * Silently authenticate and rejoin an existing game/lobby if the player has
     * one. Resolves true once reconnected, or false (tearing the socket down) if
     * the server reports no game, the handshake fails, or the check times out.
     */
    reconnectToExistingGame() {
        return __awaiter(this, void 0, void 0, function* () {
            const request = yield (0, sessionSources_1.resolveHandshake)();
            if (!request) {
                return false;
            }
            try {
                yield this.connectSocket(request);
            }
            catch (error) {
                console.error("error getting socket", error);
                return false;
            }
            this.listenForConnection();
            const socket = this.socket;
            if (!socket) {
                return false;
            }
            return new Promise((resolve) => {
                let settled = false;
                const finish = (found) => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    socket.off("connected", onFound);
                    socket.off("gameConnected", onFound);
                    socket.off("noGame", onNone);
                    clearTimeout(timer);
                    if (!found) {
                        this.disconnect();
                    }
                    resolve(found);
                };
                const onFound = () => finish(true);
                const onNone = () => finish(false);
                const timer = setTimeout(() => finish(false), RECONNECT_TIMEOUT_MS);
                socket.on("connected", onFound);
                socket.on("gameConnected", onFound);
                socket.on("noGame", onNone);
                socket.emit("reconnect");
            });
        });
    }
    disconnect() {
        var _a, _b;
        (_a = this.cleanupConnectionListeners) === null || _a === void 0 ? void 0 : _a.call(this);
        this.cleanupConnectionListeners = null;
        this.destroyConnections();
        (_b = this.socket) === null || _b === void 0 ? void 0 : _b.disconnect();
        this.socket = null;
        this.accountIdHolder.clear();
    }
    /**
     * Close the socket once a game has ended.
     */
    endSession() {
        var _a, _b;
        (_a = this.cleanupConnectionListeners) === null || _a === void 0 ? void 0 : _a.call(this);
        this.cleanupConnectionListeners = null;
        this.destroyConnections();
        (_b = this.socket) === null || _b === void 0 ? void 0 : _b.disconnect();
        this.socket = null;
    }
    connectSocket(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const socket = socket_io_client_1.io({ path: "/game/socket.io" }, { transports: ["websocket"] });
            yield new Promise((resolve, reject) => {
                socket.on("connect", () => {
                    socket.emit("authenticate", request);
                });
                const onAuthenticated = ({ error, id, }) => {
                    if (!error) {
                        if (id) {
                            this.accountIdHolder.set(id);
                        }
                        socket.off("authenticate_response", onAuthenticated);
                        resolve();
                        return;
                    }
                    socket.disconnect();
                    reject(error);
                };
                socket.on("authenticate_response", onAuthenticated);
            });
            this.socket = socket;
        });
    }
    listenForConnection() {
        const socket = this.socket;
        if (!socket) {
            return;
        }
        const onLobbyConnected = (payload) => {
            this.destroyLobbyConnection();
            const lobbyConnection = new LobbyConnection_1.LobbyConnection(socket, this.dispatch);
            lobbyConnection.handleConnected(payload);
            this.lobbyConnectionHolder.set(lobbyConnection);
        };
        const onGameConnected = (payload) => {
            this.destroyLobbyConnection();
            this.destroyGameConnection();
            const gameConnection = new GameConnection_1.GameConnection(socket, this.dispatch);
            gameConnection.handleConnected(payload);
            this.gameConnectionHolder.set(gameConnection);
            this.dispatch(models_1.ClientUi.gameStartedCommand());
        };
        socket.on("connected", onLobbyConnected);
        socket.on("gameConnected", onGameConnected);
        this.cleanupConnectionListeners = () => {
            socket.off("connected", onLobbyConnected);
            socket.off("gameConnected", onGameConnected);
        };
    }
    destroyConnections() {
        this.destroyLobbyConnection();
        this.destroyGameConnection();
    }
    destroyLobbyConnection() {
        const existing = this.lobbyConnectionHolder.peek();
        if (existing) {
            existing.destroy();
            this.lobbyConnectionHolder.clear();
        }
    }
    destroyGameConnection() {
        const existing = this.gameConnectionHolder.peek();
        if (existing) {
            existing.destroy();
            this.gameConnectionHolder.clear();
        }
    }
}
exports.SocketManager = SocketManager;
